<template>
	<div class="p-grid">
		<div class="p-col-12">
			<div class="card">
        <Toolbar class="p-mb-4">
          <template #start>
            <div style="font-size: 1.4em; color: inherit" class="p-mr-3">{{ $t('Customers') }}</div>
            <AddNewButton :label="$t('Add')" @click="addNewItem" />
          </template>
          <template #end>
            <CustomDropdown id="customer-type"
                            style="min-width: 180px; max-width: 240px;"
                            ref="customerTypesDropdown"
                            class="p-mr-2"
                            v-model="selectedType"
                            :options="CustomerTypes"
                            :disabled="dataTableIsLoading"
                            :filterPlaceholder="$t('Type')"
                            :filter="false"
                            @change="changeType"
                            :showClear="true">
              <template #value="slotProps">
                <div v-if="slotProps.value">
                  <div>{{ customerTypeLabel(slotProps.value) }}</div>
                </div>
                <span v-else>{{$t('Type')}}</span>
              </template>
              <template #option="slotProps">
                <div v-if="slotProps.option">
                  <div>{{ customerTypeLabel(slotProps.option) }}</div>
                </div>
              </template>
            </CustomDropdown>

            <SearchInput :disabled="dataTableIsLoading"
                         @change-processed-search-value="changeProcessedSearchValue"
                         v-model="searchData"
                         @show-spinner="isLoading = true"/>
          </template>
        </Toolbar>

        <Spinner v-if="isLoading"/>
        <DesktopDataTable v-else
                          :dataTableIsLoading="dataTableIsLoading"
                          :showHeader="false"
                          :tableName="$t('Customers')"
                          :itemsArrayIsEmpty="itemsArrayIsEmpty"
                          :sort="sort"
                          :showSearchInput="false"
                          :searchData="searchData"
                          :actionButtonsCount="actionButtonsCount"
                          @sort-items="sortItems"
                          :headers="[
              {name: '', title: $t('Customer'), minWidth: '180px', sortable: false},
              // {name: '', title: $t('Reg. number'), sortable: false},
              {name: 'creation_location_id', title: $t('creationLocation'), minWidth: '160px', sortable: false},
              {name: 'orders_count', class: 'ti-hummer', minWidth: '60px', sortable: true},
              {name: 'offers_count', class: 'ti-clipboard', minWidth: '60px', sortable: true},
              {name: 'cars_count', class: $store.getters.isForkliftTenant ? 'ti-truck' : 'ti-car', minWidth: '60px', sortable: true},
              {name: 'full_phone_number', title: $t('Phone'), minWidth: '180px', sortable: false},
              {name: 'email', title: $t('Email'), sortable: false},
              {name: 'discounts', title: $t('Discount'), sortable: false},
              {name: 'ordersSum', title: $t('ordersSumLabel'), width: '9%', sortable: false},
              {name: 'created_at', title: $t('Created'), sortable: false},
          ]">
          <template v-slot:body>
            <tr v-for="(item, index) of items"
                :key="index"
                :class="{'p-highlight': isItemSelected(item.id), 'updated-item': isItemUpdated(item.id)}">
              <td>
                <CustomerData :user="item"/>
<!--                <div v-if="item.reg_number && item.type === constants.userTypes.legal_entity" class="small-text p-text-secondary p-mt-1">-->
<!--&lt;!&ndash;                  {{ $t('Reg. number') }}: {{ item.reg_number }}&ndash;&gt;-->
<!--                  {{ item.reg_number }}-->
<!--                </div>-->
              </td>

              <td>
                <span v-if="item.creationLocationName">{{ item.creationLocationName }}</span>
                <i v-else class="ti-minus"></i>
              </td>

              <td>
                {{ item.orders_count ?? 0 }}
              </td>
              <td>
                {{ item.offers_count ?? 0 }}
              </td>
              <td>
                {{ item.cars_count ?? 0 }}
              </td>
              <td>
                <div v-if="item.type === constants.userTypes.legal_entity && item.primaryRepresentative">
                  <CustomerData :user="item.primaryRepresentative"/>
                  <div v-show="item.primaryRepresentative.phone_code && item.primaryRepresentative.phone_number" class="p-mt-1">
                    +{{ item.primaryRepresentative.phone_code }} {{ item.primaryRepresentative.phone_number }}
                  </div>
                </div>
                <span v-else-if="item.phone_code && item.phone_number">+{{ item.phone_code }} {{ item.phone_number }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span style="white-space: pre-wrap" v-if="item.email">
                  <span>{{ item.email }}</span>
                </span>
                <i v-else class="ti-minus"/>
              </td>

              <td class="small-text">
                <div v-if="(item.services_discount && +item.services_discount) ||
                            (item.products_discount && +item.products_discount)"
                      style="min-width: 120px; line-height: 1.3rem">
                  <div v-if="Number(item.services_discount || 0) > 0">
                    {{ $t('Services') }}: {{ formatDecimal(item.services_discount) }}%
                  </div>

                  <div v-if="Number(item.products_discount || 0) > 0">
                    {{ $t('Goods') }}: {{ formatDecimal(item.products_discount) }}%
                  </div>
                </div>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                {{ formatCurrency(item.ordersSum) }}
              </td>
              <td class="table-date-line">
                <span v-if="item.created_at">{{ formatBy(item.created_at, item.creator) }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td v-show="actionButtonsCount">
                <div class="actions">
                  <router-link :to="{ path: `/customers/${item.uid}` }">
                    <ViewButton/>
                  </router-link>
                  <EditButton :disabled="disableEditButton || dataTableIsLoading"
                              @click="editItem(item)"/>
                  <DeleteButton v-show="canDelete"
                                @click="confirmItemDelete(item, item.employee?.id)"
                                :disabled="dataTableIsLoading || cannotDeleteEmployee(item)"/>
                </div>
              </td>
            </tr>
          </template>
        </DesktopDataTable>
        <!--  Mobile DataTable -->
<!--        <MobileDataTable v-else-if="!isLoading && $store.state.tabletLayout"-->
<!--                         :tableName="$t('Customers')"-->
<!--                         :itemsArrayIsEmpty="itemsArrayIsEmpty"-->
<!--                         :showSearchInput="false"-->
<!--                         :searchData="searchData"-->
<!--                         @search-items="searchItems">-->
<!--&lt;!&ndash;          <template v-slot:multiselectHeader >&ndash;&gt;-->
<!--&lt;!&ndash;              <Checkbox v-show="$store.state.user.role === 0 || $store.state.user.role === 1" @click="selectAll" :value="0" v-model="selectAllCheckbox"/>&ndash;&gt;-->
<!--&lt;!&ndash;          </template>&ndash;&gt;-->
<!--          <template v-slot:body>-->
<!--            <tr :class="{'p-highlight': isItemSelected(item.id), 'updated-item': isItemUpdated(item.id)}"-->
<!--                v-for="(item, index) of items"-->
<!--                :key="index">-->
<!--              <td>-->
<!--&lt;!&ndash;                <div v-if="$store.state.user.role === 0 || $store.state.user.role === 1" class="p-mb-3">&ndash;&gt;-->
<!--&lt;!&ndash;                  <Checkbox :value="item.id" v-model="selectedItems"/>&ndash;&gt;-->
<!--&lt;!&ndash;                </div>&ndash;&gt;-->
<!--                <div class="p-mb-3 p-d-flex p-ai-center p-pt-2">-->
<!--                  <div class="p-text-bold mobile-datatable__title">{{ $t('Customer') }}:</div>-->
<!--                  <div class="mobile-datatable__text p-d-flex p-ai-center p-flex-wrap">-->
<!--&lt;!&ndash;                    <div>{{ item.first_name }} {{ item.last_name }}</div>&ndash;&gt;-->
<!--                    <span v-if="item.type === constants.userTypes.legal_entity && item.company_name">-->
<!--                      <span class="theme-color p-mr-2">{{  item.company_name }}</span>-->
<!--                      <span class="table-date-line">{{ item.first_name }} {{ item.last_name }}</span>-->
<!--                    </span>-->
<!--                    <span v-else>-->
<!--                      <div>{{ item.first_name }} {{ item.last_name }}</div>-->
<!--                    </span>-->
<!--                    <div><span :class="'flag&#45;&#45;datatable flag flag-' + item.flag_code" class="p-ml-1"></span></div>-->
<!--                  </div>-->
<!--                </div>-->
<!--&lt;!&ndash;                <div class="p-mb-3 p-d-flex p-ai-center">&ndash;&gt;-->
<!--&lt;!&ndash;                  <div class="p-text-bold mobile-datatable__title">{{ $t('Role') }}:</div>&ndash;&gt;-->
<!--&lt;!&ndash;                  <div class="mobile-datatable__text p-d-flex p-ai-center">{{ computedRole(item.role) }}</div>&ndash;&gt;-->
<!--&lt;!&ndash;                </div>&ndash;&gt;-->
<!--                <div class="p-mb-3 p-d-flex p-ai-center">-->
<!--                  <div class="p-text-bold mobile-datatable__title">{{ $t('Counters') }}:</div>-->
<!--                  <div class="mobile-datatable__text p-d-flex p-ai-center">-->
<!--                    <div class="p-d-flex p-ai-center">-->
<!--                      <div>-->
<!--                        <span class="ti-hummer"></span>-->
<!--                        <span class="p-text-bold p-ml-1">{{ item.orders_count || 0 }}</span>-->
<!--                      </div>-->
<!--                      <div class="p-ml-3">-->
<!--                        <span class="ti-clipboard"></span>-->
<!--                        <span class="p-text-bold p-ml-1">{{ item.offers_count || 0 }}</span>-->
<!--                      </div>-->
<!--                      <div class="p-ml-3">-->
<!--                        <span class="ti-car"></span>-->
<!--                        <span class="p-text-bold p-ml-1">{{ item.cars_count || 0 }}</span>-->
<!--                      </div>-->
<!--                    </div>-->
<!--                  </div>-->
<!--                </div>-->
<!--                <div class="p-mb-3 p-d-flex p-ai-center">-->
<!--                  <div class="p-text-bold mobile-datatable__title">{{ $t('Phone') }}:</div>-->
<!--                  <div class="mobile-datatable__text p-d-flex p-ai-center">-->
<!--                    <span v-if="item.type === constants.userTypes.legal_entity && item.company_name"><span v-if="item.company_phone_code">+</span>{{ item.company_phone_code }} {{ item.company_phone_number }}</span>-->
<!--                    <span v-else><span v-if="item.phone_code">+</span>{{ item.phone_code }} {{ item.phone_number }}</span>-->
<!--                  </div>-->
<!--                </div>-->
<!--                <div class="p-mb-3 p-d-flex p-ai-center">-->
<!--                  <div class="p-text-bold mobile-datatable__title">{{ $t('Email') }}:</div>-->
<!--                  <span class="mobile-datatable__text p-d-flex p-ai-center">-->
<!--                    <span v-if="item.type === constants.userTypes.legal_entity && item.company_name">{{ item.company_email }}</span>-->
<!--                    <span v-else>{{ item.email }}</span>-->
<!--                  </span>-->
<!--                </div>-->
<!--                <div class="p-mb-3 p-d-flex p-ai-center">-->
<!--                  <div class="p-text-bold mobile-datatable__title">{{ $t('Created') }}</div>-->
<!--                  <div class="mobile-datatable__text p-d-flex p-ai-center">-->
<!--                    <div class="table-date-line">-->
<!--                      <span v-if="item.created_at">{{ formatDate(item.created_at) }}, </span>-->
<!--                      <span v-if="item.creator" class="table-date-line">-->
<!--                        <span v-if="item.creator.type === constants.userTypes.legal_entity && item.creator.company_name">{{ item.creator.company_name }}<span> ({{ item.creator.first_name }} {{ item.creator.last_name }})</span></span>-->
<!--                        <span v-else>{{ item.creator.first_name }} {{ item.creator.last_name }}</span>-->
<!--                      </span>-->
<!--                    </div>-->
<!--                  </div>-->
<!--                </div>-->
<!--                <div class="p-m-2 p-d-flex p-jc-center">-->
<!--                  <router-link :to="{ path: `/customers/${item.id}` }"><ViewButton/></router-link>-->
<!--                  <EditButton :disabled="!checkUserCanEditCustomer(item.role) || ((disableEditButton && isItemUpdated(item.id)) || !allDataLoaded)"-->
<!--                              @click="editItem(item)" />-->
<!--                  <DeleteButton :disabled="item.role === constants.userRoles.superAdmin"-->
<!--                                @click="confirmItemDelete( item)"-->
<!--                                v-show="$store.state.user.role === constants.userRoles.superAdmin || $store.state.user.role === constants.userRoles.admin"/>-->
<!--                </div>-->
<!--              </td>-->
<!--            </tr>-->
<!--          </template>-->
<!--        </MobileDataTable>-->

        <OwnPaginator v-show="!itemsArrayIsEmpty"
                      :rows="pagination.perPage"
                      :currentPage="pagination.currentPage - 1"
                      :totalPages="pagination.pageCount"
                      :totalRecords="pagination.totalCount"
                      :rowsPerPageOptions="[5,10,25,50,100,200]"
                      :pageLinkSize="11"/>

        <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

        <CustomerModal :visible="itemModal"
                       :item="item"
                       @update-items="updateItems"
                       @close="closeItemModal"/>

        <ConfirmDeleteModal :visible="confirmItemDeleteModal"
                            :modalDataIsLoading="modalDataIsLoading"
                            @close="closeConfirmItemDeleteModal"
                            @delete-item="deleteItem"
                            :data="itemToDeleteData"/>
			</div>

		</div>
	</div>
</template>

<script>
import httpClient from '@/services/http.services'
import CustomerModal from '@/pages/users/components/CustomerModal'
import formatMixins from '@/mixins/formatMixins'
import datatableMixins from "@/mixins/datatableMixins";
import httpMixins from "@/mixins/httpMixins";
import settings from "@/settings";
import CustomerTypes from "@/translations/CustomerTypes";
import CustomerData from "@/components/CustomerData.vue";
import labelMixin from "@/mixins/labelMixin";

export default {
  mixins: [ formatMixins, datatableMixins, httpMixins, labelMixin ],
  components: { CustomerData, CustomerModal },
	data() {
		return {
      fieldsExpand: 'creator,customerStatus,ordersSum,primaryRepresentative.customerStatus,creationLocationName',
      apiLink: 'user',
      deleteItemUrl: 'user/delete',
      routePath: '/customers',
      CustomerTypes
		}
	},
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    this.checkRouteQuery()
    this.getPageData()

    this.setSelectedType()
	},
  methods: {
    getPageData() {
      this.getItems()
    },
    customerTypeLabel(type) {
      return type?.label?.[this.$i18n.locale] || type?.label?.en || ''
    },
    setSelectedType() {
      if (this.selectedTypeId) {
        const type = CustomerTypes.find(n => n.type === this.selectedTypeId)
        if (type)  this.selectedType = type
      } else {
        this.selectedType = null
      }
    },
    changeType() {
      const nextTypeId = this.selectedType ? Number(this.selectedType.type) : null

      if (this.selectedTypeId === nextTypeId) return

      this.selectedTypeId = nextTypeId

      const route = {
        route: this.$route.path,
        query: {
          ...this.$route.query,
          type: this.selectedTypeId,
        }
      }

      delete route.query.page

      if (!this.selectedTypeId) {
        delete route.query.type
      }

      this.$router.push(route)
    },
    async getItems(showSpinner = false) {
      if (!this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      this.disableEditButton = true
      if (showSpinner) {
        this.isLoading = true
      }
      const ctrl = this.setController?.('customers')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          params: {
            type: this.selectedTypeId,
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          signal: ctrl?.signal
          // data: {}
        })
        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        this.disableEditButton = false
        if (this._abortors?.customers === ctrl) {
          this._abortors.customers = null
        }
      }
    },
    addNewItem() {
			this.item = {}
			this.submitted = false
			this.itemModal = true
      this.$store.commit('toggleFirstLayer', true)
		},
    cannotDeleteEmployee(user) {
      if (!user) return true

      const targetId = Number(user?.id || 0)
      const currentId = Number(this.$store.getters.userData?.id || 0)

      if (targetId === currentId || !this.$store.getters.isOwnerOrHigher) return true


      return false
    },
	},
  computed: {
    canDelete() {
      return this.$store.getters.isOwnerOrHigher
    },
    actionButtonsCount() {
      return this.canDelete ? 3 : 2
    },
    itemToDeleteData() {
      if (!this.itemToDelete) return null
      return this.getCustomerLabel(this.itemToDelete)
    },
  }
}
</script>
